import React from "react";
import cn from "classnames";
import { generatePath, Navigate } from "react-router-dom";
import * as Team from "src/state/modules/team/types";
import Styles from "./TeamsList.module.scss";
import { Avatar } from "src/components/atoms/Avatar/Avatar";
import { Text } from "../../atoms/Text/Text";
import { Icon } from "src/components/atoms/Icon/Icon";
import { ROUTES } from "src/utils/constants/routes";
import { Clickable } from "src/components/atoms/Clickable/Clickable";
import { teamAvatarUrl } from "src/utils/team-avatar";
import { fromNow } from "src/utils/date-utils";
import { useFavoriteTeams } from "src/components/util/useFavoriteTeams";

type TeamOrOrg = Team.ListTeam & { isAdmin: boolean };

// Material "star" (filled) and "star_border" (outline) glyphs.
const STAR_FILLED = "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z";
const STAR_BORDER =
  "M22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z";

// Pin/unpin star. Lives inside the row's Clickable (a <span role=button>), so it stops click and
// keydown from bubbling up and navigating to the team page when the user just wants to (un)pin.
const FavoriteStar = ({ team }: { team: Team.ListTeam }) => {
  const { isFavorite, toggle } = useFavoriteTeams();
  const favorited = isFavorite(team.id);
  return (
    <button
      type="button"
      className={cn(Styles.favoriteButton, { [Styles.favoriteOn]: favorited })}
      aria-pressed={favorited}
      aria-label={favorited ? `Unpin ${team.name}` : `Pin ${team.name} to top`}
      title={favorited ? "Unpin team" : "Pin to top"}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        toggle(team.id);
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
        <path d={favorited ? STAR_FILLED : STAR_BORDER} />
      </svg>
    </button>
  );
};

// Freshness of the backend's last sync for this team (driven by the polling worker). Surfaces
// "Updated 5 minutes ago", or that we haven't imported the team yet.
const lastUpdatedLabel = (lastUpdated?: string | null) =>
  lastUpdated ? `Updated ${fromNow(lastUpdated)}` : "Not yet synced";

// True when the poller synced this team very recently; drives a subtle green "fresh" accent on the
// timestamp (mobile only — see TeamsList.module.scss). Ten-minute window matches the weekend poll
// cadence closely enough to flag "just updated" without staying green for hours.
const FRESH_WINDOW_MS = 10 * 60 * 1000;
const isRecentlySynced = (lastUpdated?: string | null) => {
  if (!lastUpdated) return false;
  const t = Date.parse(lastUpdated);
  return Number.isFinite(t) && Date.now() - t < FRESH_WINDOW_MS;
};

const ItemAvatar = (item: TeamOrOrg) => {
  return <Avatar.Team source={teamAvatarUrl(item.id)} sportIcon={item.sport} size={"medium"} />;
};

const ItemName = (item: TeamOrOrg) => {
  // statLevel is parenthesized ("(open)"). Desktop keeps it inline after the name; the mobile meta
  // line shows it bare and dot-separated from the timestamp.
  const statBare = (item.statLevel ?? "").replace(/[()]/g, "").trim();
  return (
    <div className={Styles.teamName}>
      <Text size={"base"} weight={"semibold"}>
        <span>
          {item.name} <span className={cn(Styles.teamStatLevel, Styles.statLevelInline)}>{item.statLevel}</span>
        </span>
      </Text>
      <div className={Styles.lastUpdated}>
        {statBare && <span className={Styles.statLevelMeta}>{statBare}</span>}
        <span className={cn(Styles.updatedText, { [Styles.fresh]: isRecentlySynced(item.lastUpdated) })}>
          {lastUpdatedLabel(item.lastUpdated)}
        </span>
      </div>
    </div>
  );
};

const ItemGroup = (item: TeamOrOrg) => {
  return (
    <div className={Styles.teamGroup}>
      <FavoriteStar team={item} />
      <div className={Styles.chevron}>
        <Icon type={"chevron"} size={"xsmall"} fill={"coolGreyDark"} />
      </div>
    </div>
  );
};

interface TeamRowProps {
  team: Team.ListTeam;
  isLastStaffTeam: boolean;
}

export const TeamRow = ({ team, isLastStaffTeam }: TeamRowProps) => {
  const [itemID, setItemID] = React.useState("");
  const userIsManagerForTeam = true;
  const handleClick = (item: Team.ListTeam) => {
    setItemID(item.id);
  };

  const makeRoute = () => generatePath(ROUTES.teams.home, { id: itemID });

  return (
    <>
      {itemID && <Navigate to={makeRoute()} />}
      <Clickable onClick={() => handleClick(team)} className={cn(Styles.teamRow, Styles.enabled)}>
        <ItemAvatar {...team} isAdmin={userIsManagerForTeam} />
        <ItemName {...team} isAdmin={userIsManagerForTeam} />
        <ItemGroup {...team} isAdmin={userIsManagerForTeam} />
      </Clickable>
      <div className={isLastStaffTeam ? Styles.staffNonStaffRowBorder : Styles.teamRowBorder} />
    </>
  );
};
